import { ClassNames, useTheme } from '@emotion/react';
import type { AccordionTab, HeaderProps } from '@presentation/components';
import { Accordion, Button } from '@presentation/components';
import { useTranslation } from '@presentation/utils';
import type { FC, TouchEvent } from 'react';
import { useCallback, useMemo, useRef, useState } from 'react';
import storeFrontIcon from '@assets/storefront.svg';
import { FaBars } from 'react-icons/fa';
import { TbX } from 'react-icons/tb';
import { LanguageSelector } from '../LanguageSelector';
import * as Styled from './styled';

const SWIPE_TO_CLOSE_DISTANCE_PIXELS = 50;

export const MobileHeader: FC<HeaderProps> = (props) => {
  const { links, showStoreLink, buttons, color, languageSelectorButtons, onStoreNavbarClick } =
    props;

  const theme = useTheme();
  const { translate } = useTranslation();
  const [isNavOpen, setIsNavOpen] = useState(false);
  const touchStartPositionX = useRef<number | null>(null);
  const touchEndPositionX = useRef<number | null>(null);

  const dismiss = useCallback(() => {
    (window?.document?.querySelector?.('[data-drawer-overlay]') as any)?.click?.();
  }, []);

  const touchStartHandler = useCallback((event: TouchEvent<HTMLDivElement>) => {
    touchEndPositionX.current = null;
    touchStartPositionX.current = event.targetTouches[0].clientX;
  }, []);

  const touchMoveHandler = useCallback((event: TouchEvent<HTMLDivElement>) => {
    touchEndPositionX.current = event.targetTouches[0].clientX;
  }, []);

  const touchEndHandler = useCallback(() => {
    if (!touchStartPositionX.current || !touchEndPositionX.current) return;
    const distance = touchStartPositionX.current - touchEndPositionX.current;
    const isRightSwipe = distance < -SWIPE_TO_CLOSE_DISTANCE_PIXELS;
    if (isRightSwipe) {
      dismiss();
    }
  }, [dismiss]);

  const shopTab: AccordionTab[] = useMemo(() => {
    if (!showStoreLink) return [];
    return [
      {
        title: translate('common.buttons.storeHeader'),
        icon: <img src={storeFrontIcon} alt="store" />,
        labelProps: { onClick: onStoreNavbarClick, htmlFor: 'mobile-header-drawer', type: 'store' },
      },
    ] as AccordionTab[];
  }, [onStoreNavbarClick, showStoreLink, translate]);

  const tabs: AccordionTab[] = useMemo(() => {
    return (links ?? [])
      .map(
        (link) =>
          ({
            title: link.label,
            labelProps: {
              ...(!link.links?.length
                ? {
                    onClick: link.onClick,
                    htmlFor: 'mobile-header-drawer',
                  }
                : {}),
            },
            content: !link.links?.length ? undefined : (
              <>
                {(link.links ?? []).map((subLink, subIndex) => (
                  <Styled.SubLink
                    key={subIndex}
                    to={subLink.link}
                    onClick={() => {
                      subLink.onClick?.();
                      dismiss();
                    }}
                  >
                    {subLink.label}
                  </Styled.SubLink>
                ))}
              </>
            ),
          } as AccordionTab),
      )
      .concat(shopTab);
  }, [dismiss, links, shopTab]);
  return (
    <Styled.Wrapper
      onTouchStart={touchStartHandler}
      onTouchMove={touchMoveHandler}
      onTouchEnd={touchEndHandler}
    >
      <Styled.OpenButton
        htmlFor="mobile-header-drawer"
        tabIndex={0}
        aria-haspopup="true"
        role="button"
        aria-controls="menu"
        color={color === 'light' ? 'blue' : 'light'}
      >
        <FaBars />
      </Styled.OpenButton>

      <Styled.Input
        type="checkbox"
        id="mobile-header-drawer"
        aria-checked={isNavOpen}
        onChange={(e) => {
          const isOpen = e.target.checked;
          setIsNavOpen(isOpen);
          document.body.style.overflow = isOpen ? 'hidden' : '';
        }}
      />

      <Styled.Overlay data-drawer-overlay htmlFor="mobile-header-drawer" />
      <Styled.Drawer data-drawer-content>
        <Styled.DrawerClose htmlFor="mobile-header-drawer">
          <TbX />
        </Styled.DrawerClose>
        <Styled.DrawerContainer alignTop={(!links).toString()}>
          <ClassNames>
            {({ css: classNameCss }) => {
              const wrapperClassname = classNameCss(Styled.accordionWrapperStyle(theme));
              const labelClassname = classNameCss(Styled.accordionLabelStyle(theme));

              return (
                <Accordion
                  wrapperClass={wrapperClassname}
                  labelClass={labelClassname}
                  tabs={tabs}
                />
              );
            }}
          </ClassNames>

          <div>
            {(buttons ?? []).map((button, index) => (
              <Styled.ButtonWrapper
                key={index}
                onClick={() => {
                  dismiss();
                  button.onClick?.();
                }}
                htmlFor="mobile-header-drawer"
              >
                <Button
                  link={button.link}
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    event.currentTarget.parentElement?.click();
                  }}
                  color={button.style === 'main' ? 'blue' : 'light'}
                >
                  {button.label}
                </Button>
              </Styled.ButtonWrapper>
            ))}

            <Styled.LanguageSelectorWrapper>
              <LanguageSelector color="dark" languageSelectorButtons={languageSelectorButtons} />
            </Styled.LanguageSelectorWrapper>
          </div>
        </Styled.DrawerContainer>
      </Styled.Drawer>
    </Styled.Wrapper>
  );
};
